<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('add-medication') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text v-if="currentRecord" class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 205px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-text-field
						class="pa-0"
						v-model="searchText"
						:label="$t('search')"
						:value="searchText"
						prepend-inner-icon="mdi-magnify"
						outlined
						dense
						clearable
						hide-details
						@keypress.enter="searchForPrescriptions" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-btn block color="dark_purple" style="color: white" @click="searchForPrescriptions">
						{{ $t('search') }}
					</v-btn>
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-radio-group v-model="selectedPrescription" dense hide-details class="my-0 pa-0">
						<v-data-table
							:loading="loading"
							:headers="headers"
							:no-data-text="$t('no-data-available')"
							:loading-text="$t('loading')"
							:items="searchedPrescriptions"
							:items-per-page="-1"
							sort-by="brandName"
							fixed-header
							:height="Math.max(parseInt(pageHeight(-269)), 250)"
							style="width: 100%; overflow-y: auto"
							dense
							class="ma-0 pa-0 data-table-outline border-radius0"
							hide-default-footer>
							<template v-slot:item="{ item, index }">
								<v-tooltip left>
									<template v-slot:activator="{ on, attrs }">
										<tr :class="getRowsForDataTable(index)" v-bind="attrs" v-on="on" @click="selectPrescription(item)">
											<td class="font-size13 text-left pl-2 pr-0" style="max-width: 160px">
												{{ item.brandName }}
											</td>
											<td class="font-size13 text-left pl-2 pr-0" style="max-width: 160px">
												{{ shortenText(item.dosageInstructions) }}
											</td>
											<td class="font-size13 text-left pl-2 pr-0" style="max-width: 80px">
												{{ item.route }}
											</td>
											<td class="font-size13 text-left pl-2 pr-0">
												<v-radio :value="item" />
											</td>
										</tr>
									</template>
									<span>{{ item.manufacturerName }}</span>
								</v-tooltip>
							</template>
						</v-data-table>
					</v-radio-group>
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-text-field outlined dense v-model="dosage" :label="$t('dosage')" hide-details />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col :cols="frequency === 'Custom / Other' ? 6 : 12">
					<v-select
						outlined
						dense
						v-model="frequency"
						:label="$t('frequency')"
						:items="frequencies"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col v-if="frequency === 'Custom / Other'" cols="6">
					<v-text-field outlined dense v-model="customFrequency" :label="$t('custom-frequency')" hide-details />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6">
					<v-select
						outlined
						dense
						v-model="prescribingDoctor"
						:label="$t('prescribing-doctor')"
						:items="doctors"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="6">
					<v-text-field
						outlined
						dense
						v-model="durationDays"
						:label="$t('duration-days')"
						hide-details
						:suffix="$t('days')"
						@keypress="filterNumbersOnly" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-textarea v-model="additionalInfo" :label="$t('additional-info')" outlined dense no-resize hide-details>
					</v-textarea>
				</v-col>
			</v-row>
		</v-card-text>
		<v-card-text
			v-else
			class="pa-4 overflow-y-auto text-center d-flex flex-column justify-center overflow-x-hidden"
			style="height: calc(100vh - 62px)">
			<v-icon color="grey" size="50" class="mb-3"> mdi-alert-circle-outline </v-icon>
			<p class="font-responsive-lg lightgrey">
				{{ $t('select-a-record') }}
			</p>
		</v-card-text>
		<v-container v-if="currentRecord" class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip v-if="!addButtonEnabled" left>
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('prescribe') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-medication-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addPrescription()" color="dark_purple" style="color: white" block>
						{{ $t('prescribe') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { getRowsForDataTable, shortenText } from '@/utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMMedicationEventAdd',
	components: { InputTimeFormatter },
	data () {
		return {
			loading: false,
			searchText: '',
			dosage: '',
			frequency: '',
			customFrequency: '',
			frequencies: [],
			period: '',
			additionalInfo: '',
			doctors: null,
			prescribingDoctor: null,
			selectedPrescription: null,
			durationDays: 0,
			route: '',
			isMedicationHasEnded: false,
			timeSpentSeconds: 0,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
			searchedPrescriptions: (state) => state.prescriptions.searchedPrescriptions,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		textareaRows () {
			return this.$vuetify.breakpoint.lgAndUp ? 30 : 19;
		},
		headers () {
			return [
				{
					text: this.$t('prescription-brand-name'),
					value: 'brandName',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('dosage'),
					value: 'dosageInstructions',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('route'),
					value: 'route',
					class: 'table-header pl-2 pr-0',
					sortable: false,
				},
				{
					text: '',
					align: 'start pl-2 pr-0',
					sortable: false,
				},
			];
		},
		addButtonEnabled () {
			return (
				this.selectedPrescription &&
				this.dosage.trim() &&
				this.frequency &&
				this.durationDays &&
				this.timeSpentSeconds &&
				this.prescribingDoctor &&
				this.timeSpentSeconds <= 7200
			);
		},
	},

	methods: {
		...mapActions({
			addMedicationEvent: 'ccmMedicationEvents/addCCMMedicationEvent',
			getMedicationFrequencies: 'ccmRecords/getMedicationFrequencies',
		}),
		getRowsForDataTable,
		shortenText,
		closeDrawer () {
			this.medicationName = '';
			this.searchText = '';
			this.dosage = '';
			this.period = '';
			this.additionalInfo = '';
			this.prescribingDoctor = null;
			this.selectedPrescription = null;
			this.route = '';
			this.isMedicationHasEnded = false;
			this.timeSpentSeconds = 0;
			this.durationDays = 0;
			this.frequency = '';
			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},

		async searchForPrescriptions () {
			this.loading = true;
			try {
				const searchBody = {
					drugName: this.searchText,
				};
				await this.$store.dispatch('prescriptions/fdaSearch', searchBody);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedMedicationEvent',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
			this.selectedPrescription = null;
			this.loading = false;
		},

		async addPrescription () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				medicationName: this.medicationName,
				dosage: this.dosage.trim(),
				frequency: this.frequency === 'Custom / Other' ? this.customFrequency : this.frequency,
				durationDays: this.durationDays,
				route: this.route,
				eventDate: new Date().toISOString(),
				eventType: '',
				staffId: this.prescribingDoctor.id,
				timeSpentSeconds: this.timeSpentSeconds,
				additionalData: '',
				genericName: this.selectedPrescription.genericName,
				productNdc: this.selectedPrescription.productNdc,
				manufacturerName: this.selectedPrescription.manufacturerName,
				warnings: this.selectedPrescription.warnings,
				adverseReactions: this.selectedPrescription.adverseReactions,
				additionalInfo: this.additionalInfo,
			};
			try {
				await this.addMedicationEvent(body);
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedMedicationEvent',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.closeDrawer();
			}
		},

		filterDoctors () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => member.roleId === 3 && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},

		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},

		selectPrescription (item) {
			this.selectedPrescription = item;
			this.medicationName = item.brandName;
			this.route = item.route;
		},
	},

	watch: {
		selectedPrescription (newPrescription) {
			if (newPrescription && newPrescription.dosageInstructions) {
				this.dosage = newPrescription.dosageInstructions;
			} else {
				this.dosage = '';
			}
		},
	},

	async mounted () {
		this.doctors = this.filterDoctors();
		const response = await this.getMedicationFrequencies();
		this.frequencies = response.data.map((f) => f.description);
	},
};
</script>
